import axios from 'axios';
import { config } from 'Constants';

/**
 * Returns authorization headers with user token
 * @returns {Object} Headers object with Content-Type and Authorization
 */
const getAuthHeaders = (additionalHeaders = {}) => {
    const user = JSON.parse(localStorage.getItem('user'));
    return {
        'Content-Type': 'application/json',
        Authorization: user.token,
        ...additionalHeaders,
    };
};

/**
 * Creates the base URL for the API
 * @param {String} endpoint - API endpoint path
 * @returns {String} Complete API URL
 */
const createApiUrl = (endpoint) => `${config.url.API_URL}/api/kahramanlar/${endpoint}`;

/**
 * Retrieves all productions with optional filtering
 * @param {Object} query - Query parameters (page, limit, isActive, etc.)
 * @returns {Promise} Axios promise with production list response
 */
export const getProductions = (query) => {
    const url = createApiUrl('productions');
    return axios.get(url, {
        headers: getAuthHeaders(),
        params: query,
    });
};

/**
 * Searches productions with filters
 * @param {Object} data - Search parameters (searchQuery, Rating, etc.)
 * @returns {Promise} Axios promise with search results
 */
export const searchProductions = (data) => {
    const url = createApiUrl('productions/search');

    // Convert arrays in data to strings
    const params = Object.entries(data).reduce((acc, [key, value]) => {
        acc[key] = Array.isArray(value) ? JSON.stringify(value) : value;
        return acc;
    }, {});

    return axios.get(url, {
        headers: getAuthHeaders(),
        params,
    });
};

/**
 * Retrieves a specific production by ID
 * @param {String} id - Production ID
 * @returns {Promise} Axios promise with production details
 */
export const getProduction = (id) => {
    const url = createApiUrl(`productions/${id}`);
    return axios.get(url, { headers: getAuthHeaders() });
};

/**
 * Updates an existing production
 * @param {String} id - Production ID to update
 * @param {Object} data - Updated production data
 * @returns {Promise} Axios promise with update response
 */
export const updateProduction = (id, data) => {
    const url = createApiUrl(`productions/${id}`);
    return axios.put(url, data, {
        headers: getAuthHeaders({ 'X-Source': 'productions' }),
    });
};

/**
 * Deletes a production
 * @param {String} id - Production ID to delete
 * @returns {Promise} Axios promise with deletion response
 */
export const deleteProduction = (id) => {
    const url = createApiUrl(`productions/${id}`);
    return axios.delete(url, { headers: getAuthHeaders() });
};

/**
 * Creates a new production
 * @param {Object} data - Production data
 * @returns {Promise} Axios promise with creation response
 */
export const postProduction = (data) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const url = createApiUrl('productions');

    // Add user information to production data
    const enrichedData = {
        ...data,
        createdBy: user.userID,
        responsibles: [user.userID],
    };

    return axios.post(url, enrichedData, { headers: getAuthHeaders() });
};

/**
 * Copies an existing production
 * @param {String} productionID - Production ID to copy
 * @returns {Promise} Axios promise with copy response
 */
export const copyProduction = (productionID) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const url = createApiUrl(`productions/${productionID}`);

    return axios({
        method: 'COPY',
        url,
        headers: {
            Authorization: user.token,
            'Access-Control-Allow-Origin': '*',
        },
    });
};

/**
 * Syncs Aaro orders with productions
 * @param {Array} aaroOrders - Array of Aaro order IDs to sync
 * @returns {Promise} Axios promise with sync results
 */
export const syncAaroOrdersWithProductions = (aaroOrders) => {
    const url = createApiUrl('productions/sync-aaro-orders');
    return axios.post(url, { aaroOrders }, { headers: getAuthHeaders() });
};
